import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown, Plus, Settings2, UserPlus } from "lucide-react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CreateTeamDialog } from "@/components/workspaces/CreateTeamDialog";
import { InviteMembersDialog } from "@/components/workspaces/InviteMembersDialog";
import { cn } from "@/lib/utils";
import { canManageWorkspace } from "@/lib/workspacePermissions";

export const workspaceInitials = (name: string | undefined) => {
  if (!name) return "?";
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (words[0]?.[0] ?? "").concat(words[1]?.[0] ?? "").toUpperCase() || "?";
};

export const WorkspaceAvatar = ({ name, className }: { name?: string; className?: string }) => (
  <span
    className={cn(
      "grid place-items-center w-[31px] h-[31px] shrink-0 rounded-lg bg-selected text-plum text-xs font-semibold",
      className,
    )}
  >
    {workspaceInitials(name)}
  </span>
);

interface SidebarWorkspaceSwitchProps {
  linkState?: unknown;
  // "card" is the sidebar block; "crumb" is the compact topbar trigger
  variant?: "card" | "crumb";
}

export const SidebarWorkspaceSwitch = ({ linkState, variant = "card" }: SidebarWorkspaceSwitchProps) => {
  const { workspaces, activeWorkspace, switchWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const isPersonal = activeWorkspace?.type === "personal";
  // Inviting from personal adds people to it in place (the dialog handles the plan check); in a team only owners and admins invite.
  const canInvite = canManageWorkspace(activeWorkspace);

  const inviteMembers = () => {
    setOpen(false);
    if (isPersonal) setInviteOpen(true);
    else navigate("/workspace/settings?invite=1", { state: linkState });
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {variant === "card" ? (
            <button
              type="button"
              className="flex w-full items-center gap-2.5 rounded-[10px] border border-line bg-canvas p-2.5 mb-3 text-left transition-colors hover:border-input"
              aria-label={activeWorkspace ? `Switch workspace. Current: ${activeWorkspace.name}` : "Switch workspace"}
              aria-busy={!activeWorkspace}
            >
              {/* Until the workspace resolves there is nothing true to say, so no name and no role guess */}
              {activeWorkspace ? (
                <>
                  <WorkspaceAvatar name={activeWorkspace.name} />
                  <span className="min-w-0 flex-1">
                    <strong className="block truncate text-xs font-semibold text-foreground">
                      {activeWorkspace.name}
                    </strong>
                    {activeWorkspace.role && (
                      <small className="block text-[11px] text-muted-foreground capitalize">{activeWorkspace.role}</small>
                    )}
                  </span>
                </>
              ) : (
                <>
                  <span data-testid="workspace-switch-placeholder" className="w-[31px] h-[31px] shrink-0 rounded-lg bg-muted animate-pulse" />
                  <span className="min-w-0 flex-1 grid gap-1.5">
                    <span className="block h-3 w-24 rounded bg-muted animate-pulse" />
                    <span className="block h-2.5 w-16 rounded bg-muted animate-pulse" />
                  </span>
                </>
              )}
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex max-w-[170px] items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground"
              aria-label="Switch workspace"
              aria-busy={!activeWorkspace}
            >
              {activeWorkspace ? (
                <span className="truncate">{activeWorkspace.name}</span>
              ) : (
                <span data-testid="workspace-switch-placeholder" className="block h-3 w-20 rounded bg-muted animate-pulse" />
              )}
              <ChevronDown className="w-3 h-3 shrink-0" />
            </button>
          )}
        </PopoverTrigger>
        {/* Capped to the viewport so the Create buttons stay reachable; only the workspace list scrolls */}
        <PopoverContent
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-[10030] flex w-72 max-h-[var(--radix-popover-content-available-height)] flex-col rounded-xl border-line p-2"
        >
          <div className="shrink-0 px-2 pt-1 pb-2">
            <p className="font-display text-sm font-semibold text-foreground">Your workspaces</p>
            <p className="text-xs text-muted-foreground leading-snug">
              Each workspace has its own projects, team and billing.
            </p>
          </div>
          <div className="grid min-h-0 gap-1 overflow-y-auto overscroll-contain">
            {workspaces.map((ws) => {
              const selected = ws.id === activeWorkspace?.id;
              return (
                <div key={ws.id} className="grid gap-0.5">
                <button
                  type="button"
                  data-testid={`sidebar-workspace-option-${ws.id}`}
                  onClick={() => {
                    switchWorkspace(ws.id);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex items-center gap-2.5 rounded-lg p-2 text-left transition-colors",
                    selected ? "bg-selected" : "hover:bg-soft",
                  )}
                >
                  <WorkspaceAvatar name={ws.name} className="w-7 h-7 text-[11px]" />
                  <span className="min-w-0 flex-1">
                    <strong className="block truncate text-[13px] font-medium text-foreground">{ws.name}</strong>
                    {ws.role && <small className="block text-[11px] text-muted-foreground capitalize">{ws.role}</small>}
                  </span>
                  {selected && <Check className="w-4 h-4 text-plum" aria-label="Selected workspace" />}
                </button>
                {selected && canInvite && (
                  <button
                    type="button"
                    data-testid="sidebar-invite-members"
                    onClick={inviteMembers}
                    className="ml-[38px] inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[12px] font-medium text-plum transition-colors hover:bg-soft"
                  >
                    <UserPlus className="w-3.5 h-3.5" /> Invite members
                  </button>
                )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 grid shrink-0 grid-cols-2 gap-2 border-t border-line pt-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                // A separate workspace with its own subscription; inviting into the current one is the entry above
                setCreateTeamOpen(true);
              }}
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-gradient-sunset px-2.5 py-2 text-xs font-semibold text-white"
            >
              <Plus className="w-3.5 h-3.5 shrink-0" />
              Create workspace
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(isPersonal ? "/profile" : "/workspace/settings", { state: linkState });
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-input bg-surface px-3 py-2 text-xs font-semibold text-foreground hover:bg-soft"
            >
              <Settings2 className="w-3.5 h-3.5" /> Settings
            </button>
          </div>
        </PopoverContent>
      </Popover>
      {/* Stays mounted through the conversion, since a successful invite flips the workspace to "team" before the dialog closes */}
      {activeWorkspace && (
        <InviteMembersDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          workspace={activeWorkspace}
          onCreateSeparateWorkspace={() => setCreateTeamOpen(true)}
        />
      )}
      <CreateTeamDialog open={createTeamOpen} onOpenChange={setCreateTeamOpen} />
    </>
  );
};
